import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check, Laptop } from "lucide-react";
import {
  EmptyState,
  KindBadge,
  ProgressBar,
  RUN_BUTTON,
  SectionCard,
  ShowMore,
  StageChip,
} from "@/components/new-look/blocks";
import { STAGES, STAGE_ORDER, type Stage } from "@/components/new-look/labels";
import { NEEDS_YOU_TEXT, stageOf, type IdeaStageResult } from "@/components/new-look/stage";
import {
  useApproveEpicRequest,
  useEpicRequestInterview,
  useIdea,
  useUpdateEpicRequest,
} from "@/hooks/queries/use-epic-requests";
import { useAuthStore } from "@/stores/auth-store";
import {
  useCancelJob,
  useCreateFollowUp,
  useJob,
  useJobChain,
  usePostTurn,
  useUploadAttachment,
} from "@/hooks/queries/use-mailbox";
import { useSessionEvents } from "@/hooks/useSessionEvents";
import { useKeyInterviewDriver } from "@/hooks/queries/use-key-interview-driver";
import { usePageTitle } from "@/hooks/use-page-title";
import type { EpicRequest } from "@/lib/api/epic-requests";
import { cn } from "@/lib/utils";
import { clarityTone, factsFor, progressOf, updatedAgo } from "./idea-helpers";
import { ApiError } from "@/lib/api/client";
import type { Job } from "@/lib/api/mailbox";
import { looksLikeSecret, toIdeaRunsOn } from "@mycompbox/shared";
import { NeedsInputCard } from "@/components/mailbox/needs-input-card";
import { WaitingPullRequests } from "@/components/mailbox/waiting-pull-requests";
import { RunOnMachineDialog } from "@/components/epic-requests/run-on-machine-dialog";
import { ClarityPanel } from "./clarity-panel";
import { ProgressRing } from "./progress-ring";
import { IdeaGrowth } from "./idea-growth";
import { StartIdeaHelper } from "./idea-helper-start";
import { AutomationBar, useAutoRun, useIdeaMode, type AutoStep } from "./automation-bar";
import { IdeaHistory, milestones } from "./idea-history";
import { useEpic } from "@/hooks/queries/use-epics";
import { BuildDecision } from "@/components/new-look/build-decision";
import {
  OWNER_TURN_BODY,
  OwnerChecks,
  isOwnerTurn,
  ownerTurnTitle,
  partsDoneText,
  useOwnerChecks,
} from "@/components/new-look/owner-checks";
import type { SessionEvent } from "@mycompbox/shared";
import { buildStream, type StreamItem } from "./idea-stream";
import { IdeaStreamPanel, type StreamConfirm } from "./idea-stream-panel";

/**
 * One idea's page (docs/new-look/PLAN.md, step 4.1): where it is, what it's
 * about, the one next thing to do, and the one-stream window with the
 * conversation and the build's activity (4.2). Pull requests (4.7) come next. Every next step
 * either has a real target (planning run, build run, blueprint) or stays here
 * and focuses the stream's message box (ENG-365).
 */

function StageRail({ current, closed }: { current: Stage; closed: boolean }) {
  const at = STAGE_ORDER.indexOf(current);
  return (
    <ol aria-label="Stages" className="flex gap-2">
      {STAGE_ORDER.map((s, i) => {
        const done = !closed && (i < at || (current === "done" && i === at));
        const now = !closed && i === at && current !== "done";
        return (
          <li
            key={s}
            aria-current={now ? "step" : undefined}
            className={cn(
              "flex min-w-0 items-center gap-2 rounded-xl border px-2.5 py-2 sm:flex-1 sm:px-3",
              now ? "flex-1" : "shrink-0",
              now ? "border-primary bg-accent" : "border-border bg-background"
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                done
                  ? "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200"
                  : now
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : String(i + 1)}
            </span>
            <span
              className={cn(
                "truncate text-sm",
                now
                  ? "font-semibold text-foreground"
                  : "sr-only text-muted-foreground sm:not-sr-only"
              )}
            >
              {STAGES[s]}
            </span>
            {done && <span className="sr-only">(done)</span>}
          </li>
        );
      })}
    </ol>
  );
}

interface NextStep {
  title: string;
  body: string;
  to: string | null;
  action: string | null;
  urgent: boolean;
  /** Start it from here with today's send-to-machine: plan (make the blueprint) or build. */
  dispatch?: "plan" | "build";
  /** The run button's words when it isn't the usual "Start building". */
  runLabel?: string;
  /** Offer Approve here (a pending idea; only its requester or an admin sees the button). */
  approve?: boolean;
  /** Decide the build here: mark it done or say what's missing. */
  decide?: boolean;
  /** The action stays on this page: it focuses the stream's message box. */
  focus?: boolean;
  /** The Idea helper is talking in this page's chat: jump to it. */
  chat?: boolean;
  /** No conversation yet: start the Idea helper here, in this page's chat. */
  discuss?: boolean;
}

/** The one thing to do next, and where to do it. */
export function nextStepFor(
  idea: EpicRequest,
  result: IdeaStageResult,
  conversationJobId: string | null
): NextStep {
  const facts = idea.facts;
  const blueprint = facts?.epic ? `/blueprints/${facts.epic.id}` : null;
  const buildRun = facts?.buildJob ? `/mailbox/jobs/${facts.buildJob.id}` : null;
  const planRun = facts?.planJob ? `/mailbox/jobs/${facts.planJob.id}` : null;
  const conversation = conversationJobId ? `/mailbox/jobs/${conversationJobId}` : null;
  const partsTotal = facts?.epic?.featureCount ?? 0;
  const partsDone = facts?.epic?.completedFeatureCount ?? 0;
  const partsLeft = Math.max(0, partsTotal - partsDone);
  const partsText = `${String(partsDone)} of ${String(partsTotal)} ${partsTotal === 1 ? "part" : "parts"} done`;

  if (result.closed) {
    return {
      title: "This idea was turned down",
      body: "It's kept here for the record.",
      to: null,
      action: null,
      urgent: false,
    };
  }
  switch (result.needsYou) {
    case "answer_idea_helper":
      return {
        title: NEEDS_YOU_TEXT.answer_idea_helper,
        body: "The Idea helper asked you something. Answer it in the chat.",
        to: null,
        action: null,
        urgent: true,
        chat: true,
      };
    case "answer_blueprint_maker":
      return {
        title: NEEDS_YOU_TEXT.answer_blueprint_maker,
        body: "Planning is paused on a question.",
        to: planRun,
        action: "Answer",
        urgent: true,
        focus: !planRun,
      };
    case "answer_builder":
      return {
        title: NEEDS_YOU_TEXT.answer_builder,
        body: "The build is paused on a question.",
        to: buildRun ?? blueprint,
        action: "Answer",
        urgent: true,
        focus: !(buildRun ?? blueprint),
      };
    case "merge_prs":
      return {
        title: NEEDS_YOU_TEXT.merge_prs,
        body:
          partsLeft > 0 && !facts?.buildJob
            ? `The build stopped early: ${partsText}. Merging saves that work; then continue the build to finish the rest.`
            : "The build's pull requests are ready for you.",
        to: buildRun ?? blueprint,
        action: (buildRun ?? blueprint) ? "Review and merge" : null,
        urgent: true,
      };
    case "decide_build":
      return {
        title: NEEDS_YOU_TEXT.decide_build,
        body: "The final check wasn't sure the build is complete. Read what it found, then decide.",
        to: null,
        action: null,
        urgent: true,
        decide: true,
      };
    case "review_build":
      return {
        title: NEEDS_YOU_TEXT.review_build,
        body: "The build is done and checked. Review it and merge.",
        to: blueprint,
        action: blueprint ? "Review" : null,
        urgent: true,
      };
    case "make_blueprint":
      return {
        title: NEEDS_YOU_TEXT.make_blueprint,
        body: "The idea is approved. Send it to the Blueprint maker.",
        to: null,
        action: null,
        urgent: true,
        dispatch: "plan",
      };
    default:
      break;
  }
  switch (result.stage) {
    case "idea":
      if (idea.status === "pending" && idea.complianceScore?.tier === "pass") {
        return {
          title: "Ready to approve",
          body: "It's clear enough to plan. Approve it, then make the blueprint.",
          to: null,
          action: null,
          urgent: true,
          approve: true,
          chat: !!conversation,
        };
      }
      return conversation
        ? {
            title: "The Idea helper is shaping it",
            body: "Keep answering in the chat until the idea is clear, then approve it.",
            to: null,
            action: null,
            urgent: false,
            approve: idea.status === "pending",
            chat: true,
          }
        : {
            title: "Waiting for review",
            body: "Talk it through with the Idea helper in the chat, or approve it when it's clear.",
            to: null,
            action: null,
            urgent: false,
            focus: true,
            approve: idea.status === "pending",
            discuss: idea.status === "pending",
          };
    case "blueprint":
      return planRun
        ? {
            title: "The Blueprint maker is working",
            body: "It's turning the idea into parts and tasks.",
            to: planRun,
            action: "Watch",
            urgent: false,
          }
        : {
            title: "Blueprint ready",
            body: "Check the plan, then start building.",
            to: blueprint,
            action: blueprint ? "Open the blueprint" : null,
            urgent: false,
            ...(blueprint ? { dispatch: "build" as const } : {}),
          };
    case "build":
      // Nothing running and parts left (e.g. a build that stopped early and was merged).
      if (!buildRun && facts?.epic && partsLeft > 0) {
        return {
          title: "Continue the build",
          body: `${partsText}. The rest hasn't been built yet.`,
          to: blueprint,
          action: null,
          urgent: true,
          dispatch: "build",
          runLabel: "Continue the build",
        };
      }
      return {
        title: "Building",
        body: buildRun
          ? "The Builder is working on it now."
          : "Parts are done; the rest is waiting to run.",
        to: buildRun ?? blueprint,
        action: buildRun ? "Watch the build" : blueprint ? "Open the blueprint" : null,
        urgent: false,
      };
    default:
      return {
        title: "Shipped",
        body: "Built, checked and merged.",
        to: blueprint,
        action: blueprint ? "See the blueprint" : null,
        urgent: false,
      };
  }
}

/** Who a message goes to: the running build, else the planning run, else the interview. */
/** How the message box talks to the build chain's latest job (ENG-364). */
export type ComposerMode = "talk" | "note" | "reply" | "followUp";

export interface StreamTarget {
  jobId: string;
  label: string;
  stoppable: string | null;
  mode: ComposerMode;
}

/**
 * Who the message box talks to. Planning and the interview work as before; a
 * build follows the newest job in its chain: running → a note it reads at its
 * next step, waiting → a reply, finished or failed → a follow-up, stopping or
 * stopped → no box. Only the build's owner may send; everyone else gets
 * `{ readOnly: ownerName }`.
 */
export function streamTarget(
  idea: EpicRequest,
  interviewJobId: string | null,
  interviewStatus: string | undefined,
  userId?: string | null
): StreamTarget | { readOnly: string } | null {
  const facts = idea.facts;
  const latest = facts?.latestBuildJob;
  if (!facts?.planJob && latest) {
    if (latest.status === "cancelling" || latest.status === "cancelled") return null;
    if (userId && latest.owner.id !== userId) return { readOnly: latest.owner.name };
    if (latest.status === "done" || latest.status === "failed")
      return {
        jobId: latest.id,
        label: "Ask the Builder for a follow-up",
        stoppable: null,
        mode: "followUp",
      };
    if (latest.status === "awaiting_input" || latest.status === "waiting")
      return {
        jobId: latest.id,
        label: "Reply to the Builder",
        stoppable: "the build",
        mode: "reply",
      };
    return { jobId: latest.id, label: "Message the Builder", stoppable: "the build", mode: "note" };
  }
  if (facts?.buildJob)
    return {
      jobId: facts.buildJob.id,
      label: "Message the Builder",
      stoppable: "the build",
      mode: "note",
    };
  if (facts?.planJob)
    return {
      jobId: facts.planJob.id,
      label: "Message the Blueprint maker",
      stoppable: "planning",
      mode: "note",
    };
  if (interviewJobId && interviewStatus !== "cancelled")
    return {
      jobId: interviewJobId,
      label: "Message the Idea helper",
      stoppable: null,
      mode: "talk",
    };
  return null;
}

/** One local, unsaved line from CompBox (e.g. the answer to /status). */
function note(text: string, tone: StreamItem["tone"] = "plain"): StreamItem {
  const at = new Date().toISOString();
  return { id: `local:${at}:${text}`, at, kind: "activity", who: "compbox", text, tone };
}

/** A one-line summary for /status. */
export function statusLine(idea: EpicRequest): string {
  const result = stageOf(factsFor(idea));
  const epic = idea.facts?.epic;
  const parts = epic
    ? `, ${String(epic.completedFeatureCount)} of ${String(epic.featureCount)} parts done`
    : "";
  const prs = idea.facts?.openPullRequests
    ? `, ${String(idea.facts.openPullRequests)} pull requests open`
    : "";
  const needs = result.needsYou ? `. Needs you: ${NEEDS_YOU_TEXT[result.needsYou]}` : "";
  return `${result.closed ? "Rejected" : STAGES[result.stage]}${parts}${prs}${needs}.`;
}

/**
 * What a run is paused on, from the build and planning jobs. The Idea helper's
 * replies are not listed: they already show in the stream, and every reply
 * leaves the interview "awaiting input".
 */
export function waitingQuestion(
  build: Job | undefined,
  plan: Job | undefined
): { who: string; job: Job } | null {
  for (const [job, who] of [
    [build, "The Builder"],
    [plan, "The Blueprint maker"],
  ] as const) {
    if (job?.status === "waiting" && job.waitingReason === "needs_input") return { who, job };
  }
  return null;
}

/** After a plan or build is sent: stay here and bring the stream into view. */
function showStream(): void {
  document.getElementById("idea-stream")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function focusMessageBox(): void {
  document.getElementById("stream-input")?.focus();
}

/** The question a run is paused on, with an Answer button that jumps to the message box. */
function QuestionCard({ question }: { question: NonNullable<ReturnType<typeof waitingQuestion>> }) {
  const lastAsk = [...question.job.turns]
    .reverse()
    .find((t) => t.role === "agent")
    ?.content.trim();
  const request = question.job.needsInput;
  return (
    <section
      aria-label="Waiting for you"
      className="flex flex-col gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 shadow-[0_8px_24px_rgba(245,158,11,0.12)] dark:border-amber-500/40 dark:bg-amber-500/10 sm:p-5"
    >
      <span className="text-xs font-bold uppercase tracking-wide text-amber-900 dark:text-amber-200">
        Paused · waiting for you
      </span>
      {request ? (
        <NeedsInputCard request={request} />
      ) : (
        <>
          <p className="text-sm text-amber-950 dark:text-amber-100">{question.who} asked:</p>
          <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-foreground">
            {lastAsk ?? "An answer from you."}
          </p>
        </>
      )}
      <button
        type="button"
        onClick={focusMessageBox}
        className="inline-flex min-h-11 items-center self-start rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
      >
        Answer
      </button>
    </section>
  );
}

/** The conversation and activity for one idea, from its interview, its running jobs and its blueprint's events. */
function IdeaStream({
  idea,
  interviewJobId,
  events,
  eventsLoading,
}: {
  idea: EpicRequest;
  interviewJobId: string | null;
  /** The blueprint's session events (loaded once by the page). */
  events: readonly SessionEvent[];
  eventsLoading: boolean;
}) {
  const facts = idea.facts;
  const interview = useJob(interviewJobId ?? undefined);
  // An interview on an API key has no daemon: this page asks for each reply.
  const keyStepError = useKeyInterviewDriver(interview.data);
  const plan = useJob(facts?.planJob?.id);
  // ENG-364: the build and every follow-up run it continues, newest first.
  const chain = useJobChain(facts?.buildJob?.id ?? facts?.latestBuildJob?.id);
  const build = chain[0] ?? { data: undefined, isLoading: false, failureCount: 0 };
  const epicId = facts?.epic?.id;
  const [local, setLocal] = useState<StreamItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<StreamConfirm | null>(null);

  const userId = useAuthStore((s) => s.user?.id ?? null);
  const resolved = streamTarget(idea, interviewJobId, interview.data?.status, userId);
  const readOnlyOwner = resolved && "readOnly" in resolved ? resolved.readOnly : null;
  const target = resolved && "jobId" in resolved ? resolved : null;
  const post = usePostTurn(target?.mode === "followUp" ? "" : (target?.jobId ?? ""));
  const followUp = useCreateFollowUp(target?.mode === "followUp" ? target.jobId : "");
  const sendTo = target?.mode === "followUp" ? followUp : post;
  const question = waitingQuestion(build.data, plan.data);
  // Files go with a message to the Idea helper or with an answer to a run's
  // question (ER-133); a working build's notes and a new follow-up are text only.
  const attachTo =
    target &&
    (target.mode === "talk" || target.mode === "reply" || (target.mode === "note" && question))
      ? target.jobId
      : null;
  const uploadTo = useUploadAttachment(attachTo ?? "");
  const cancel = useCancelJob();

  const items = [
    ...buildStream({
      interviewTurns: interview.data?.turns,
      planTurns: plan.data?.turns,
      buildTurns: chain.flatMap((q) => q.data?.turns ?? []),
      events,
    }),
    ...local,
  ].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
  // A job this person can't read (someone else's interview) fails rather than
  // loads; stop waiting on it after the first failure.
  const waiting = (q: { isLoading: boolean; failureCount: number }) =>
    q.isLoading && q.failureCount === 0;
  const loading =
    waiting(interview) || waiting(plan) || waiting(build) || (!!epicId && eventsLoading);
  const live =
    facts?.buildJob != null || facts?.planJob != null || interview.data?.status === "running";

  const commands = target?.stoppable ? ["/stop", "/status"] : ["/status"];

  const onSend = async (text: string, attachmentIds?: string[]) => {
    setError(null);
    if (text.startsWith("/") && !attachmentIds) {
      const command = text.split(/\s+/)[0]?.toLowerCase();
      if (command === "/status") {
        setLocal((l) => [...l, note(statusLine(idea))]);
      } else if (command === "/stop" && target?.stoppable) {
        const what = target.stoppable;
        const jobId = target.jobId;
        setConfirm({
          text: `Stop ${what}? Pull requests already opened stay open.`,
          confirmLabel: "Stop it",
          onCancel: () => {
            setConfirm(null);
          },
          onConfirm: () => {
            setConfirm(null);
            cancel.mutate(jobId, {
              onSuccess: () => {
                setLocal((l) => [...l, note(`You stopped ${what}.`)]);
              },
              onError: (e) => {
                setError(e instanceof ApiError ? e.message : `Couldn't stop ${what}.`);
              },
            });
          },
        });
      } else {
        setLocal((l) => [
          ...l,
          note(`Commands: ${commands.join(", ")}. Anything else is sent as a message.`),
        ]);
      }
      return;
    }
    const send = async () => {
      try {
        await sendTo.mutateAsync({ content: text, ...(attachmentIds && { attachmentIds }) });
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Couldn't send. Try again.");
        throw e;
      }
    };
    // Same guard as today's reply box: a run's messages go to an AI on a computer.
    const secret = target?.stoppable ? looksLikeSecret(text) : { secret: false };
    if (secret.secret) {
      return new Promise<void>((resolve, reject) => {
        setConfirm({
          text: `${secret.reason ?? "That looks like a password or key."} Don't paste secrets here; add keys in Settings.`,
          confirmLabel: "Send anyway",
          onCancel: () => {
            setConfirm(null);
            reject(new Error("not sent"));
          },
          onConfirm: () => {
            setConfirm(null);
            send().then(resolve, reject);
          },
        });
      });
    }
    await send();
  };

  return (
    <>
      {question && <QuestionCard question={question} />}
      <IdeaStreamPanel
        items={items}
        loading={loading}
        live={live}
        title={`compbox · ${idea.identifier ?? idea.title}`}
        composer={
          target
            ? {
                placeholder: target.label,
                onSend,
                sending: sendTo.isPending,
                error: error ?? (keyStepError ? `${keyStepError} Retrying automatically.` : null),
                commands,
                ...(attachTo && { upload: (file: File) => uploadTo.mutateAsync({ file }) }),
              }
            : undefined
        }
        readOnly={readOnlyOwner ? `Only ${readOnlyOwner} can message this build.` : undefined}
        confirm={confirm}
      />
    </>
  );
}

/**
 * The blueprint's pull requests (step 4.7): today's list, in merge order, with
 * checks, Mark ready, merge and "All merged — continue" for a stopped build.
 */
function IdeaPullRequests({
  epicId,
  buildJobId,
}: {
  epicId: string;
  buildJobId: string | undefined;
}) {
  const build = useJob(buildJobId);
  return (
    // Hidden while the list renders nothing (no pull requests and nothing to continue).
    <div className="overflow-hidden rounded-2xl border border-border bg-background empty:hidden [&>section]:border-t-0">
      <WaitingPullRequests epicId={epicId} {...(build.data ? { job: build.data } : {})} />
    </div>
  );
}

/** Approve a pending idea, for its requester or an admin (same rule as the classic page). */
function ApproveIdea({ idea, onApproved }: { idea: EpicRequest; onApproved: () => void }) {
  const user = useAuthStore((s) => s.user);
  const approve = useApproveEpicRequest();
  const allowed = !!user && (user.isGlobalAdmin || user.id === idea.requestedById);
  if (!allowed) {
    return (
      <p className="text-sm text-muted-foreground">
        Waiting for {idea.requestedBy?.name ?? "the person who asked"} to approve it.
      </p>
    );
  }
  return (
    <div className="mt-1 flex flex-col gap-1.5">
      <button
        type="button"
        disabled={approve.isPending}
        onClick={() => {
          approve.mutate(idea.id, { onSuccess: onApproved });
        }}
        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        <Check className="h-4 w-4" aria-hidden="true" />
        {approve.isPending ? "Approving…" : "Approve"}
      </button>
      {approve.isError && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-300">
          {approve.error instanceof ApiError
            ? approve.error.message
            : "Couldn't approve. Try again."}
        </p>
      )}
    </div>
  );
}

function About({ idea }: { idea: EpicRequest }) {
  const sd = idea.structuredDesc;
  const parts: [string, string | undefined][] = [
    ["The problem", sd?.problemStatement],
    ["The idea", sd?.proposedSolution],
    ["Who it helps", sd?.targetAudience],
    ["How we'll know it worked", sd?.successMetrics],
  ];
  const shown = parts.filter((p): p is [string, string] => !!p[1]?.trim());
  return (
    <SectionCard title="About this idea">
      {shown.length > 0 ? (
        <ShowMore long={shown.reduce((n, [, t]) => n + t.length, 0) > 700}>
          <dl className="flex flex-col gap-4">
            {shown.map(([label, text]) => (
              <div key={label}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {label}
                </dt>
                <dd className="mt-1 whitespace-pre-line text-[15px] leading-relaxed text-foreground">
                  {text}
                </dd>
              </div>
            ))}
          </dl>
        </ShowMore>
      ) : idea.description?.trim() ? (
        <p className="line-clamp-[12] whitespace-pre-line text-[15px] leading-relaxed text-foreground">
          {idea.description}
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Nothing written yet. The interview fills this in.
        </p>
      )}
    </SectionCard>
  );
}

/** Autopilot or Manual, and where the idea runs, above the idea (automation-bar.tsx). */
function IdeaAutomation({ idea, step, onStarted }: { idea: EpicRequest; step: AutoStep; onStarted: () => void }) {
  const [mode, setMode] = useIdeaMode(idea.id);
  const runsOn = toIdeaRunsOn(idea.runsOn);
  const note = useAutoRun(mode, step, onStarted, runsOn);
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const update = useUpdateEpicRequest();
  const [saveError, setSaveError] = useState<string | null>(null);
  // Only the idea's owner can change where it runs.
  const onRunsOn =
    userId === idea.requestedById
      ? (next: "computer" | "cloud") => {
          setSaveError(null);
          update.mutateAsync({ id: idea.id, input: { runsOn: next } }).catch((e: unknown) => {
            setSaveError(e instanceof ApiError ? e.message : "Couldn't change where this idea runs. Try again.");
          });
        }
      : undefined;
  return (
    <AutomationBar
      mode={mode}
      onMode={setMode}
      note={saveError ?? note}
      runsOn={runsOn}
      {...(onRunsOn ? { onRunsOn } : {})}
    />
  );
}

export function IdeaPage() {
  const { requestId = "" } = useParams<{ requestId: string }>();
  const { data: idea, isLoading, isError, refetch } = useIdea(requestId);
  // Owner-only: other people simply don't get the conversation link.
  const { data: interview } = useEpicRequestInterview(requestId);
  usePageTitle(idea?.title ?? "Idea");
  // Loaded once here for both the stream and How it grew.
  const blueprintId = idea?.facts?.epic?.id ?? "";
  const { events, isLoading: eventsLoading } = useSessionEvents({
    epicId: blueprintId,
    enabled: !!blueprintId,
  });
  const { data: epicDetail } = useEpic(blueprintId);
  const owner = useOwnerChecks(blueprintId);

  if (isLoading) {
    return (
      <div aria-busy="true" aria-label="Loading the idea" className="flex flex-col gap-4">
        <div className="h-10 w-2/3 animate-pulse rounded-xl bg-muted" />
        <div className="h-14 animate-pulse rounded-xl bg-muted" />
        <div className="h-64 animate-pulse rounded-2xl bg-muted" />
      </div>
    );
  }
  if (isError || !idea) {
    return (
      <EmptyState
        title="Couldn't open this idea"
        body="It may have been removed, or you may not have access."
        action={
          <Link
            to="/ideas"
            className="inline-flex min-h-11 items-center rounded-lg border border-border px-4 text-sm font-medium hover:bg-muted"
          >
            Back to All ideas
          </Link>
        }
      />
    );
  }

  const result = stageOf(factsFor(idea));
  const epic = idea.facts?.epic ?? null;
  // Only owner checks left: it's the owner's turn, not "Shipped".
  const ownerTurn = isOwnerTurn(result, !!(idea.facts?.buildJob ?? idea.facts?.planJob), {
    done: epic?.completedFeatureCount ?? 0,
    total: epic?.featureCount ?? 0,
    owner: owner.parts.length,
    checksLeft: owner.checksLeft,
  });
  const progress = result.closed
    ? null
    : ownerTurn && epic && epic.featureCount > 0
      ? Math.round((epic.completedFeatureCount / epic.featureCount) * 100)
      : progressOf(idea, result.stage);
  const clarity = idea.complianceScore?.overall;
  const next: NextStep = ownerTurn
    ? {
        title: ownerTurnTitle(owner.checksLeft),
        body: OWNER_TURN_BODY,
        to: null,
        action: null,
        urgent: true,
      }
    : nextStepFor(idea, result, interview?.jobId ?? null);

  return (
    <div className="flex flex-col gap-5">
      <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground">
        <Link to="/ideas" className="hover:text-foreground">
          All ideas
        </Link>
        <span aria-hidden="true"> › </span>
        <span>{idea.identifier ?? "Idea"}</span>
      </nav>

      <IdeaAutomation
        idea={idea}
        step={
          next.dispatch === "plan"
            ? { kind: "plan", epicRequestId: idea.id }
            : next.dispatch === "build" && epic
              ? {
                  kind: "orchestrate",
                  epicId: epic.id,
                  fastMode: epicDetail?.team?.fastMode ?? false,
                }
              : null
        }
        onStarted={() => {
          void refetch();
          showStream();
        }}
      />

      <header className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <KindBadge kind="software" className="mt-1" />
          <h1 className="min-w-0 text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-3xl">
            {idea.title}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {result.closed ? (
            <span className="inline-flex min-h-6 items-center rounded-full bg-muted px-2.5 text-xs font-semibold">
              Rejected
            </span>
          ) : (
            <StageChip stage={result.stage} />
          )}
          {result.needsYou && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
              {NEEDS_YOU_TEXT[result.needsYou]}
            </span>
          )}
          {ownerTurn && (
            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-900 dark:bg-amber-500/15 dark:text-amber-200">
              Your turn
            </span>
          )}
          <span>{idea.team?.name ?? "Personal"}</span>
          {idea.requestedBy?.name && <span>· asked by {idea.requestedBy.name}</span>}
          <span>· {updatedAgo(idea.updatedAt)}</span>
        </div>
      </header>

      <div className="flex flex-col items-stretch gap-4 rounded-2xl border border-border bg-background p-4 sm:flex-row sm:items-center sm:p-5">
        <div className="min-w-0 flex-1">
          <StageRail current={result.stage} closed={result.closed} />
        </div>
        {!result.closed && (
          <ProgressRing
            epicId={epic?.id ?? null}
            percent={result.stage === "done" ? 100 : progress}
            mode={
              result.stage === "done"
                ? "done"
                : result.needsYou || ownerTurn
                  ? "paused"
                  : idea.facts?.buildJob
                    ? "running"
                    : epic
                      ? "not_started"
                      : "estimating"
            }
          />
        )}
      </div>

      {!result.closed && (
        <IdeaGrowth
          said={idea.description ?? ""}
          epicId={epic?.id ?? null}
          stage={result.stage}
          live={!!idea.facts?.buildJob}
        />
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="flex min-w-0 flex-col gap-5">
          <div id="idea-stream" className="flex scroll-mt-24 flex-col gap-5">
            <IdeaStream
              idea={idea}
              interviewJobId={interview?.jobId ?? null}
              events={events}
              eventsLoading={eventsLoading}
            />
          </div>
          {epic && <IdeaPullRequests epicId={epic.id} buildJobId={idea.facts?.buildJob?.id} />}
          <About idea={idea} />
          <IdeaHistory items={milestones(idea, epicDetail ?? null, events)} />
        </div>

        <aside className="flex min-w-0 flex-col gap-4">
          <section
            aria-label="Next step"
            className={cn(
              "flex flex-col gap-2 rounded-2xl border p-4 sm:p-5",
              next.urgent
                ? "border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10"
                : "border-border bg-background"
            )}
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Next
            </span>
            <h2 className="text-lg font-semibold text-foreground">{next.title}</h2>
            <p className="text-sm text-muted-foreground">{next.body}</p>
            {ownerTurn && <OwnerChecks parts={owner.parts} />}
            {next.decide && epic && (
              <BuildDecision
                epicId={epic.id}
                onDecided={() => {
                  void refetch();
                  showStream();
                }}
              />
            )}
            {next.chat && (
              <button
                type="button"
                onClick={showStream}
                className="mt-1 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-semibold text-foreground hover:bg-muted"
              >
                Go to the chat
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {next.discuss && (
              <StartIdeaHelper
                epicRequestId={idea.id}
                onStarted={() => {
                  void refetch();
                  showStream();
                }}
              />
            )}
            {next.approve && (
              <ApproveIdea
                idea={idea}
                onApproved={() => {
                  void refetch();
                }}
              />
            )}
            {next.dispatch === "plan" ? (
              <div className={cn("mt-1", RUN_BUTTON)}>
                <RunOnMachineDialog
                  jobType="start_execution"
                  payload={{ kind: "plan", epicRequestId: idea.id }}
                  targetRemote={idea.team?.gitRemote ?? null}
                  preferredRunWith={toIdeaRunsOn(idea.runsOn) === "cloud" ? "cloud" : "computer"}
                  onStarted={() => {
                    void refetch();
                    showStream();
                  }}
                >
                  <Laptop className="mr-2 h-4 w-4" aria-hidden="true" />
                  Make the blueprint
                </RunOnMachineDialog>
              </div>
            ) : next.dispatch === "build" && epic ? (
              <div className={cn("mt-1 flex flex-col gap-2", RUN_BUTTON)}>
                <RunOnMachineDialog
                  jobType="start_execution"
                  payload={{ kind: "orchestrate", epicId: epic.id }}
                  targetRemote={epicDetail?.team?.gitRemote ?? idea.team?.gitRemote ?? null}
                  defaultFastMode={epicDetail?.team?.fastMode ?? false}
                  defaultCheckMode={epicDetail?.team?.checkMode}
                  preferredRunWith={toIdeaRunsOn(idea.runsOn) === "cloud" ? "cloud" : "computer"}
                  onStarted={() => {
                    void refetch();
                    showStream();
                  }}
                >
                  <Laptop className="mr-2 h-4 w-4" aria-hidden="true" />
                  {next.runLabel ?? "Start building"}
                </RunOnMachineDialog>
                {next.to && (
                  <Link to={next.to} className="text-center text-sm text-primary hover:underline">
                    Check the blueprint first
                  </Link>
                )}
              </div>
            ) : next.focus && next.action ? (
              <button
                type="button"
                onClick={focusMessageBox}
                className={cn(
                  "mt-1 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  next.urgent && !next.approve
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "border border-border bg-background text-foreground hover:bg-muted"
                )}
              >
                {next.action}
              </button>
            ) : (
              next.to &&
              next.action && (
                <Link
                  to={next.to}
                  className={cn(
                    "mt-1 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    next.urgent && !next.approve
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "border border-border bg-background text-foreground hover:bg-muted"
                  )}
                >
                  {next.action}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              )
            )}
          </section>

          {/* While it's still an idea, how clear it is matters most. */}
          {result.stage === "idea" && !result.closed && <ClarityPanel idea={idea} />}

          <SectionCard title="At a glance">
            <dl className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-muted-foreground">Clarity</dt>
                <dd
                  className={cn(
                    "text-lg font-semibold tabular-nums",
                    clarity === undefined ? "text-muted-foreground" : clarityTone(clarity)
                  )}
                >
                  {clarity ?? "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Parts done</dt>
                <dd className="text-lg font-semibold tabular-nums text-foreground">
                  {epic
                    ? partsDoneText(
                        epic.completedFeatureCount,
                        epic.featureCount,
                        owner.parts.length
                      )
                    : "—"}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="text-muted-foreground">Open pull requests</dt>
                <dd className="text-lg font-semibold tabular-nums text-foreground">
                  {idea.facts?.openPullRequests ?? 0}
                </dd>
              </div>
            </dl>
            {progress !== null && (
              <ProgressBar
                value={progress}
                label="Build progress"
                stage={result.stage}
                className="mt-4"
              />
            )}
          </SectionCard>

          <nav aria-label="More" className="flex flex-col gap-1 text-sm">
            {epic && (
              <Link
                to={`/blueprints/${epic.id}`}
                className="min-h-11 content-center rounded-lg px-2 text-primary hover:bg-muted"
              >
                Open the blueprint
              </Link>
            )}
          </nav>
        </aside>
      </div>
    </div>
  );
}
